/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDomainData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading domain data, tenants, and aliases.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getDomainById } from '../api/telephonyDomainsApi';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { Domain, DomainFormState, TenantOption } from '../types';

export const DEFAULT_DOMAIN_FORM: DomainFormState = {
  id: undefined,
  domainName: '',
  domainDescription: '',
  tenantId: undefined,
  domainEnabled: true,
  language: 'en',
  timezone: 'UTC',
  countryCode: '1',
  certificateId: null,
  isPrimary: false,
  serviceType: 'pbx'
};

function sanitizeDomainForm(state: DomainFormState): DomainFormState {
  return {
    id: state.id,
    domainName: state.domainName.trim(),
    domainDescription: state.domainDescription.trim(),
    tenantId: state.tenantId,
    domainEnabled: Boolean(state.domainEnabled),
    language: state.language.trim() || 'en',
    timezone: state.timezone.trim() || 'UTC',
    countryCode: state.countryCode.trim() || '1',
    certificateId: state.certificateId ?? null,
    isPrimary: state.isPrimary,
    serviceType: state.serviceType || 'pbx'
  };
}

export interface CertificateOption {
  id: number;
  name: string;
  commonName: string;
  status: string;
}

export function useDomainData(
  id: string | undefined,
  duplicateId: string | null,
  mode: 'create' | 'edit',
  resetParam?: string | null
) {
  const { user } = useAuthStore();
  const { showError, showInfo } = useNotifications();
  const tenantSlug = user?.tenant?.slug ?? 'main';

  const [formData, setFormData] = useState<DomainFormState>(() => ({ ...DEFAULT_DOMAIN_FORM }));
  const [originalData, setOriginalData] = useState<DomainFormState>(() => ({ ...DEFAULT_DOMAIN_FORM }));
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [certificates, setCertificates] = useState<CertificateOption[]>([]);
  const [loadingCertificates, setLoadingCertificates] = useState(true);

  const domainLoadedRef = useRef<string | null>(null);
  const tenantsLoadedKeyRef = useRef<string | null>(null);
  const tenantsAbortControllerRef = useRef<AbortController | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);

  // Use refs for functions to avoid re-renders
  const setFormDataRef = useRef(setFormData);
  const setOriginalDataRef = useRef(setOriginalData);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  // Update refs when functions change
  useEffect(() => {
    setFormDataRef.current = setFormData;
    setOriginalDataRef.current = setOriginalData;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [setFormData, setOriginalData, showError, showInfo]);

  // Load tenants
  const loadTenants = useCallback(
    async (force = false) => {
      const fetchKey = `${tenantSlug}:${user?.id ?? 'anon'}`;
      if (!force && tenantsLoadedKeyRef.current === fetchKey) {
        return;
      }
      // Don't set the key here - set it AFTER successful fetch

      tenantsAbortControllerRef.current?.abort();
      const controller = new AbortController();
      tenantsAbortControllerRef.current = controller;

      setLoadingTenants(true);
      try {
        const response = await fetch(buildApiUrl('/api/tenants?limit=100'), {
          headers: getApiHeaders(tenantSlug, user?.id),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const tenantList = Array.isArray(payload.data) ? payload.data : [];
        const mappedTenants: TenantOption[] = tenantList
          .map((tenant: { id: number | string; name?: string; slug?: string }) => ({
            id: typeof tenant.id === 'number' ? tenant.id : (Number.isNaN(Number(tenant.id)) ? tenant.id : Number(tenant.id)),
            name: tenant.name ?? tenant.slug ?? '—',
            slug: tenant.slug ?? String(tenant.id ?? '')
          }))
          .filter(tenant => tenant.id !== undefined && tenant.id !== null);

        setTenants(mappedTenants);
        // Only set the key after successful load
        tenantsLoadedKeyRef.current = fetchKey;
      } catch (error) {
        const isAbort =
          error instanceof DOMException
            ? error.name === 'AbortError'
            : (error as any)?.name === 'AbortError';
        if (isAbort) {
          return;
        }
        tenantsLoadedKeyRef.current = null;
        showError('Failed to load tenants');
        setTenants([]);
      } finally {
        if (tenantsAbortControllerRef.current === controller) {
          tenantsAbortControllerRef.current = null;
        }
        setLoadingTenants(false);
      }
    },
    [tenantSlug, user?.id, showError]
  );

  // Load domain data
  const loadDomain = useCallback(async () => {
    const loadId = mode === 'edit' ? id : duplicateId;
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
    const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;

    // If reset param is present and not yet processed, force reset
    if (hasResetParam) {
      formResetRef.current = false;
      domainLoadedRef.current = null;
      // Clear the lastIdRef to ensure we detect the change
      lastIdRef.current = undefined;
      // Mark this reset param as processed
      resetParamProcessedRef.current = resetParam;
    }

    if (!loadId || loadId === 'new') {
      if (mode === 'create') {
        // Reset form when navigating to new (either initial load or after save)
        if (hasResetParam || !formResetRef.current || idChangedToNew || domainLoadedRef.current === null) {
          formResetRef.current = true;
          lastIdRef.current = currentId;
          domainLoadedRef.current = 'create-initialized';
          setFormDataRef.current({ ...DEFAULT_DOMAIN_FORM });
          setOriginalDataRef.current({ ...DEFAULT_DOMAIN_FORM });
        } else {
          lastIdRef.current = currentId;
        }
      }
      return;
    }

    if (domainLoadedRef.current === loadId && !hasResetParam && !idChangedToNew) {
      lastIdRef.current = currentId;
      return;
    }

    domainLoadedRef.current = loadId;
    lastIdRef.current = currentId;

    try {
      const response = await getDomainById(loadId, tenantSlug, user?.id);
      const domainData: Domain = response.data;

      const isDuplicate = Boolean(duplicateId);
      const loadedForm: DomainFormState = sanitizeDomainForm({
        id: isDuplicate ? undefined : (domainData.id ?? loadId),
        domainName: isDuplicate && domainData.domainName ? `${domainData.domainName}_copy` : (domainData.domainName ?? ''),
        domainDescription: domainData.domainDescription ?? '',
        tenantId: domainData.tenantId,
        domainEnabled: domainData.domainEnabled ?? true,
        language: domainData.language ?? 'en',
        timezone: domainData.timezone ?? 'UTC',
        countryCode: domainData.countryCode ?? '1',
        certificateId: domainData.certificateId ?? null,
        isPrimary: domainData.isPrimary ?? false,
        serviceType: domainData.serviceType || 'pbx'
      });

      formResetRef.current = false;
      setFormDataRef.current(loadedForm);
      if (mode === 'edit' && !isDuplicate) {
        setOriginalDataRef.current(loadedForm);
      } else {
        setOriginalDataRef.current({ ...DEFAULT_DOMAIN_FORM });
      }

      // Show duplicate hint notification
      if (isDuplicate) {
        showInfoRef.current('Domain duplicated. Review and save changes.');
      }
    } catch (error) {
      showErrorRef.current('Failed to load domain');
    }
  }, [duplicateId, id, mode, tenantSlug, user?.id, resetParam]);

  // Load data on mount
  useEffect(() => {
    loadDomain();
  }, [loadDomain]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Cleanup
  useEffect(() => {
    return () => {
      tenantsAbortControllerRef.current?.abort();
    };
  }, []);

  // Load certificates
  const loadCertificates = useCallback(async () => {
    setLoadingCertificates(true);
    try {
      const response = await fetch(buildApiUrl('/api/maintenance/certificates'), {
        headers: getApiHeaders(tenantSlug, user?.id)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      const certList = Array.isArray(payload.data) ? payload.data : [];
      const mappedCerts: CertificateOption[] = certList
        .filter((c: CertificateOption) => c.status === 'ACTIVE')
        .map((c: CertificateOption) => ({
          id: c.id,
          name: c.name,
          commonName: c.commonName,
          status: c.status
        }));
      setCertificates(mappedCerts);
    } catch (error) {
      console.error('Failed to load certificates:', error);
      setCertificates([]);
    } finally {
      setLoadingCertificates(false);
    }
  }, [tenantSlug, user?.id]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  return {
    formData,
    setFormData,
    originalData,
    setOriginalData,
    tenants,
    loadingTenants,
    certificates,
    loadingCertificates
  };
}

